import _slicedToArray from "/root/Etro/frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _createForOfIteratorHelper from "/root/Etro/frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import React, { useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useId, useWindowEvent, useFocusReturn } from '@mantine/hooks';
import { getDefaultZIndex, useComponentDefaultProps } from '@mantine/styles';
import { ModalBaseProvider } from './ModalBase.context.js';
import { ModalBaseCloseButton } from './ModalBaseCloseButton/ModalBaseCloseButton.js';
import { ModalBaseOverlay } from './ModalBaseOverlay/ModalBaseOverlay.js';
import { ModalBaseContent } from './ModalBaseContent/ModalBaseContent.js';
import { ModalBaseHeader } from './ModalBaseHeader/ModalBaseHeader.js';
import { ModalBaseTitle } from './ModalBaseTitle/ModalBaseTitle.js';
import { ModalBaseBody } from './ModalBaseBody/ModalBaseBody.js';
import { NativeScrollArea } from './NativeScrollArea/NativeScrollArea.js';
import { useLockScroll } from './use-lock-scroll.js';
import useStyles from './ModalBase.styles.js';
import { OptionalPortal } from '../Portal/OptionalPortal.js';
import { Box } from '../Box/Box.js';
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;

var __defNormalProp = function __defNormalProp(obj, key, value) {
  return key in obj ? __defProp(obj, key, {
    enumerable: true,
    configurable: true,
    writable: true,
    value
  }) : obj[key] = value;
};

var __spreadValues = function __spreadValues(a, b) {
  for (var prop in b || (b = {})) {
    if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }

  if (__getOwnPropSymbols) {
    var _iterator = _createForOfIteratorHelper(__getOwnPropSymbols(b)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var prop = _step.value;
        if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  return a;
};

var __spreadProps = function __spreadProps(a, b) {
  return __defProps(a, __getOwnPropDescs(b));
};

var __objRest = function __objRest(source, exclude) {
  var target = {};

  for (var prop in source) {
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0) target[prop] = source[prop];
  }

  if (source != null && __getOwnPropSymbols) {
    var _iterator2 = _createForOfIteratorHelper(__getOwnPropSymbols(source)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var prop = _step2.value;
        if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop)) target[prop] = source[prop];
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
  }

  return target;
};

var ModalBaseDefaultProps = {
  closeOnClickOutside: true,
  withinPortal: true,
  lockScroll: true,
  trapFocus: true,
  returnFocus: true,
  closeOnEscape: true,
  keepMounted: false,
  zIndex: getDefaultZIndex("modal"),
  padding: "md",
  size: "md",
  shadow: "xl"
};

function ModalBase(props) {
  var _a = useComponentDefaultProps("ModalBase", ModalBaseDefaultProps, props),
      opened = _a.opened,
      onClose = _a.onClose,
      children = _a.children,
      closeOnClickOutside = _a.closeOnClickOutside,
      __staticSelector = _a.__staticSelector,
      transitionProps = _a.transitionProps,
      withinPortal = _a.withinPortal,
      portalProps = _a.portalProps,
      keepMounted = _a.keepMounted,
      target = _a.target,
      zIndex = _a.zIndex,
      lockScroll = _a.lockScroll,
      trapFocus = _a.trapFocus,
      closeOnEscape = _a.closeOnEscape,
      returnFocus = _a.returnFocus,
      padding = _a.padding,
      shadow = _a.shadow,
      id = _a.id,
      size = _a.size,
      variant = _a.variant,
      classNames = _a.classNames,
      unstyled = _a.unstyled,
      styles = _a.styles,
      className = _a.className,
      others = __objRest(_a, ["opened", "onClose", "children", "closeOnClickOutside", "__staticSelector", "transitionProps", "withinPortal", "portalProps", "keepMounted", "target", "zIndex", "lockScroll", "trapFocus", "closeOnEscape", "returnFocus", "padding", "shadow", "id", "size", "variant", "classNames", "unstyled", "styles", "className"]);

  var _useStyles = useStyles(null, {
    name: __staticSelector,
    classNames,
    styles,
    unstyled,
    variant,
    size
  }),
      classes = _useStyles.classes,
      cx = _useStyles.cx;

  var _id = useId(id);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      titleMounted = _useState2[0],
      setTitleMounted = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      bodyMounted = _useState4[0],
      setBodyMounted = _useState4[1];

  var transitionDuration = typeof (transitionProps == null ? void 0 : transitionProps.duration) === "number" ? transitionProps == null ? void 0 : transitionProps.duration : 200;
  var shouldLockScroll = useLockScroll({
    opened,
    transitionDuration
  });
  useWindowEvent("keydown", function (event) {
    if (!trapFocus && event.key === "Escape" && closeOnEscape) {
      onClose();
    }
  });
  useFocusReturn({
    opened,
    shouldReturnFocus: trapFocus && returnFocus
  });
  return /* @__PURE__ */React.createElement(OptionalPortal, __spreadProps(__spreadValues({}, portalProps), {
    withinPortal,
    target
  }), /* @__PURE__ */React.createElement(ModalBaseProvider, {
    value: {
      __staticSelector,
      opened,
      onClose,
      closeOnClickOutside,
      transitionProps: __spreadProps(__spreadValues({}, transitionProps), {
        duration: transitionDuration,
        keepMounted
      }),
      zIndex,
      padding,
      id: _id,
      getTitleId: function getTitleId() {
        return "".concat(_id, "-title");
      },
      getBodyId: function getBodyId() {
        return "".concat(_id, "-body");
      },
      titleMounted,
      bodyMounted,
      setTitleMounted,
      setBodyMounted,
      trapFocus,
      closeOnEscape,
      shadow,
      stylesApi: {
        name: __staticSelector,
        size,
        variant,
        classNames,
        styles,
        unstyled
      }
    }
  }, /* @__PURE__ */React.createElement(RemoveScroll, {
    enabled: shouldLockScroll && lockScroll
  }, /* @__PURE__ */React.createElement(Box, __spreadValues({
    className: cx(classes.root, className)
  }, others), children))));
}

ModalBase.CloseButton = ModalBaseCloseButton;
ModalBase.Overlay = ModalBaseOverlay;
ModalBase.Content = ModalBaseContent;
ModalBase.Header = ModalBaseHeader;
ModalBase.Title = ModalBaseTitle;
ModalBase.Body = ModalBaseBody;
ModalBase.NativeScrollArea = NativeScrollArea;
export { ModalBase, ModalBaseDefaultProps };